import classNames from 'classnames';
import React from 'react';
import { defaultContentAreaRange } from '../content-area/content-area';
import { useContentArea } from '../content-area/content-area-context';
import { withinOrWiderThan } from '../ranges/ranges';
import { ContentGridContext, useContentGrid } from './content-grid-context';
import styles from "./content-grid.module.css";
/**
 * Props for the `ContentGrid` component
 */

export const contentGridColumnsByRange = range => withinOrWiderThan(range, 'md') ? 12 : 4;
/**
 * ### ContentGrid
 *
 * Creates a CSS grid within a layout content area.
 *
 * @remarks
 *
 * `ContentGrid` creates the standard layout grid for page content areas. From
 * content area ranges `xxxs` to `sm`, it renders 4 columns. From ranges `md`
 * and up, it renders 12 columns.
 *
 * `ContentGrid` must be rendered under a `ContentArea` component in the
 * React tree. To automatically adjust the number columns in the grid, it
 * accesses context and uses container queries based on the `ContentArea`.
 * (Some layouts automatically include `ContentArea` in the React tree.)
 *
 * `ContentGrid` should be the immediate parent of `ContentGridItem`
 * components. The CSS grid it establishes requires that grid items are
 * immediate children.
 *
 * `ContentGrid` will consume the full width of its parent element.
 *
 * @example
 * An example of a simple content area grid of three items:
 * ```tsx
 * <ContentGrid>
 *     <ContentGridItem defaultSpan={2} wideSpan={3}>...</ContentGridItem>
 *     <ContentGridItem defaultSpan={2} wideSpan={3}>...</ContentGridItem>
 *     <ContentGridItem defaultSpan={4} wideSpan={6}>...</ContentGridItem>
 * </ContentGrid>
 * ```
 *
 * ### Nesting
 *
 * `ContentGrid` supports grid nesting for complex DOM structures. When
 * constraints from styling or component management create intermediary
 * elements between a `ContentGrid` and `ContentGridItems`, render a new
 * `ContentGrid` to re-establish the CSS grid. `ContentGrid` will
 * automatically consume all of the columns available through its parent
 * items in the React tree.
 *
 * @example
 * For example, here is a complex, nested grid where `ContentGrid`
 * re-establishes the CSS grid above grid items:
 *
 * ```tsx
 * <ContentGrid>
 *     <ContentGridItem defaultSpan={2} wideSpan={3} ... />
 *     <ContentGridItem defaultSpan={2} wideSpan={3} ... />
 *     <ContentGridItem defaultSpan={4} wideSpan={6} className="foo-item">
 *         <div className="ud-background-foo">
 *             <div className="ud-border-foo">
 *                 <!-- Cannot add grid item here because of the div parent -->
 *                 <ContentGrid>
 *                    <!--
 *                        Re-establishes CSS grid and automatically consumes
 *                        all columns available from ContentGridItem.foo-item
 *                    -->
 *                    <ContentGridItem defaultSpan={2} ... />
 *                    <ContentGridItem defaultSpan={2} wideSpan={4} ... />
 *                 </ContentGrid>
 *             </div>
 *         </div>
 *     </ContentGridItem>
 * </ContentGrid>
 * ```
 *
 * @internalRemarks
 *
 * `ContentGrid` will always provide context values for `columnCount`, even if
 * it is rendered outside a `ContentArea`. It will default to 4 columns unless
 * the container range dictates otherwise.
 */

export const ContentGrid = _ref => {
  let {
    className,
    children
  } = _ref;
  const {
    range
  } = useContentArea();
  const {
    columnCount: contextColumnCount
  } = useContentGrid(); // If there are no other `ContentGrid` components in the React tree above
  // this instance, then this component is the _root_ of the grid. That means
  // it is responsible for automatically adjusting the number of columns
  // it renders based on the content area range.

  const isRootGrid = contextColumnCount === null;
  const columnCount = isRootGrid ? contentGridColumnsByRange(range !== null && range !== void 0 ? range : defaultContentAreaRange) : // If this is not the root of the grid, then simply consume the number of
  // columns available from the context and re-establish the CSS grid.
  contextColumnCount;
  const contentGridContextValue = {
    // On the first render, range cannot be known via context. It is assumed
    // to be `xxxs` and is updated on the second render. But, CSS is able to
    // detect the content area accurately with container queries. Consequently,
    // the column count in CSS and in JavaScript might be out of sync for the
    // first render.
    columnCount,
    range
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.grid, className, {
      [styles.root]: isRootGrid
    })
  }, /*#__PURE__*/React.createElement(ContentGridContext.Provider, {
    value: contentGridContextValue
  }, children));
};
//# sourceMappingURL=content-grid.react-component.js.map